import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  DoorOpen, ArrowRight, Wifi, Car, MapPin, UtensilsCrossed, Home as HomeIcon,
  Siren, Trash2, LogOut, Sparkles, ScrollText, Star, Building2, QrCode,
  Check, Copy, Wand2, MessageCircle, Smartphone, Zap, Globe, Heart, LayoutGrid, ChevronDown,
  Link2, PencilRuler, Share2, ChevronLeft, ChevronRight, CheckCircle2,
} from "lucide-react";
import { PricingSection, PricingCompact } from "@/components/Pricing";
import { FaqSection } from "@/components/FaqSection";
import { SiteFooter } from "@/components/SiteFooter";
import { VideoDemo } from "@/components/VideoDemo";
import { TimeSaved } from "@/components/TimeSaved";
import { Logo } from "@/components/Brand";
import { ConciergeIcon } from "@/components/ConciergeIcon";
import { CTA_PRIMARY, TRIAL_MICROCOPY } from "@/lib/plans";
import { DEMO } from "@/lib/demoImages";
import { GUIDE_THEMES } from "@/lib/sections";
import { useSeo } from "@/lib/seo";
import { sb } from "@/lib/text";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay }} className={className}>
    {children}
  </motion.div>
);

const Img = (props) => <img loading="lazy" decoding="async" {...props} />;

const FEATURES = [
  { img: DEMO.gallery[4], icon: Wifi, title: "Wi-Fi", chip: "Copia password", desc: "La password sempre a portata di mano." },
  { img: DEMO.cover, icon: DoorOpen, title: "Check-in", chip: "Dalle 15:00", desc: "Istruzioni chiare, anche con foto." },
  { img: DEMO.duomo, icon: MapPin, title: "Come arrivare", chip: "Apri mappa", desc: "Indicazioni e mappe sempre disponibili." },
  { img: DEMO.restaurant, icon: UtensilsCrossed, title: "Scopri i dintorni", chip: "8 min a piedi", desc: "I tuoi posti preferiti nella guida." },
  { img: DEMO.gallery[5], icon: Car, title: "Parcheggio", chip: "Pin mappa", desc: "Dove e come parcheggiare." },
  { img: DEMO.cafe, icon: HomeIcon, title: "Guida della casa", chip: "Come si usa", desc: "Elettrodomestici spiegati semplici." },
  { img: DEMO.gallery[2], icon: ScrollText, title: "Regole & Rifiuti", chip: "Sempre chiare", desc: "Regole della casa e differenziata." },
  { img: DEMO.gallery[1], icon: Siren, title: "Emergenze", chip: "Numeri utili", desc: "Contatti importanti a portata di tocco." },
];

const STEPS = [
  { n: "01", t: "Crea o importa", d: "Parti da zero, importa il tuo annuncio oppure lascia che l'AI ti aiuti a creare la guida." },
  { n: "02", t: "Personalizza", d: "Foto, contenuti, servizi e consigli: personalizza ogni dettaglio e crea un'esperienza davvero tua." },
  { n: "03", t: "Pubblica", d: "La tua guida è subito online, sempre aggiornata e ottimizzata per lo smartphone dei tuoi ospiti." },
  { n: "04", t: "Condividi", d: "Un link o un QR code. I tuoi ospiti hanno tutto a portata di mano, senza scaricare alcuna app." },
];
const STEP4_PHOTO = "https://images.unsplash.com/photo-1665249934445-1de680641f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const RETURN_CARDS = [
  { icon: Smartphone, title: "Nessuna app", accent: "da scaricare", desc: "Usa StayGuide direttamente dal tuo browser, su qualsiasi dispositivo." },
  { icon: Zap, title: "Pronta in", accent: "pochi minuti", desc: "Crea, personalizza e pubblica la tua guida digitale in meno tempo di quanto pensi." },
  { icon: Globe, title: "Disponibile in", accent: "italiano e inglese", desc: "Comunica con i tuoi ospiti nella loro lingua, senza complicazioni." },
];

export default function Landing() {
  const [billing, setBilling] = useState("monthly");
  useSeo({
    title: "StayGuide — La guida digitale intelligente per i tuoi ospiti",
    description: "Crea una guida digitale per la tua struttura con check-in, Wi-Fi, regole, consigli locali e Concierge AI. Meno messaggi, più autonomia per i tuoi ospiti.",
    path: "/",
  });
  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans overflow-x-hidden">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo className="h-10 sm:h-11" />
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login" className="text-sm font-semibold text-slate-600 px-3 py-2">Accedi</Link>
            <Link to="/signup" data-testid="nav-signup" className="text-sm font-semibold bg-blue-500 text-white rounded-full px-4 py-2 hover:bg-blue-600 transition-colors">Inizia ora</Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 pt-12 pb-8 lg:grid lg:grid-cols-2 lg:gap-10 lg:items-center">
        <div className="text-center lg:text-left">
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-3 py-1.5"><Sparkles className="w-3.5 h-3.5" /> La guida intelligente per i tuoi ospiti</span>
          <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.08]">
            Tutto quello che i tuoi ospiti chiedono.<br />
            <span className="text-blue-500">Prima ancora che debbano chiedertelo.</span>
          </h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-md mx-auto lg:mx-0">
            Crea in pochi minuti una guida digitale completa per la tua struttura: check-in, Wi-Fi, istruzioni, regole, consigli locali e molto altro.
          </p>
          <div className="mt-5 flex items-start gap-2.5">
            <span className="mt-0.5 w-6 h-6 rounded-full bg-emerald-50 grid place-items-center shrink-0"><Check className="w-4 h-4 text-emerald-500" /></span>
            <div>
              <p className="text-base sm:text-lg font-semibold text-slate-800 leading-snug">Nessuna app da scaricare.</p>
              <p className="text-sm text-slate-500 leading-snug mt-0.5">{sb("Un link o un QR code. Tutto il soggiorno a portata di smartphone.")}</p>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 lg:justify-start justify-center">
            <Link to="/signup?plan=trial" data-testid="hero-cta" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-blue-500 text-white font-semibold rounded-full px-6 py-3.5 shadow-lg shadow-blue-500/25">{CTA_PRIMARY} <ArrowRight className="w-4 h-4" /></Link>
            <a href="#come-funziona" onClick={(e) => { e.preventDefault(); document.getElementById("come-funziona")?.scrollIntoView({ behavior: "smooth" }); }} data-testid="hero-secondary" className="text-sm font-semibold text-slate-600 inline-flex items-center gap-1 hover:text-slate-900 transition-colors">Scopri come funziona <ArrowRight className="w-3.5 h-3.5" /></a>
          </div>
          <p data-testid="hero-microcopy" className="mt-3 text-xs text-slate-400 inline-flex items-center gap-1.5 justify-center lg:justify-start"><Check className="w-3.5 h-3.5 text-blue-500" /> {TRIAL_MICROCOPY}</p>
        </div>
        <Reveal className="mt-12 lg:mt-0 flex justify-center relative" delay={0.1}>
          <div className="absolute -top-4 right-6 bg-white rounded-2xl shadow-xl border border-slate-100 px-3 py-2 z-20 hidden sm:flex items-center gap-2 text-sm font-semibold"><QrCode className="w-4 h-4 text-slate-900" /> Scansiona & apri</div>
          <PhoneHome />
        </Reveal>
      </section>

      <VideoDemo />

      <p className="max-w-2xl mx-auto px-5 pb-4 text-center text-lg text-slate-700 leading-relaxed">
        E con il <span className="font-semibold text-blue-600">Concierge AI</span>, i tuoi ospiti possono semplicemente chiedere ciò di cui hanno bisogno e ricevere subito la risposta.
      </p>

      {/* HAI GIÀ UN ACCOUNT */}
      <section className="max-w-6xl mx-auto px-5 py-16" data-testid="returning-section">
        <Reveal className="text-center">
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl">
            Hai <span className="text-blue-500">già</span> un account?
          </h2>
          <p className="mt-3 text-slate-500 max-w-md mx-auto text-base">Bentornato! Accedi e continua da dove hai lasciato.</p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-3 gap-5">
          {RETURN_CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="h-full rounded-[1.75rem] bg-slate-50/80 border border-slate-100 p-8 text-center flex flex-col items-center transition-shadow hover:shadow-lg hover:shadow-blue-500/5">
                <div className="w-20 h-20 rounded-full bg-blue-50 grid place-items-center text-blue-500 mb-6"><c.icon className="w-9 h-9" strokeWidth={1.7} /></div>
                <p className="font-display font-bold text-xl text-slate-900 leading-tight">{c.title}</p>
                <p className="font-script text-[1.7rem] text-blue-500 leading-tight mt-0.5">{c.accent}</p>
                <p className="text-sm text-slate-500 mt-4 leading-relaxed max-w-[15rem]">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.12} className="mt-8 flex justify-center">
          <div className="inline-flex items-center gap-2.5 bg-blue-50 text-blue-700 rounded-full px-5 py-3 text-sm font-medium text-center">
            <Heart className="w-4 h-4 fill-blue-500 text-blue-500 shrink-0" /> {sb("Sicuro, veloce e sempre con te. Accedi e gestisci tutto in un unico posto.")}
          </div>
        </Reveal>
        <div className="mt-8 text-center">
          <Link to="/login" data-testid="returning-login" className="inline-flex items-center justify-center gap-2 bg-blue-500 text-white font-semibold rounded-full px-7 py-3.5 shadow-lg shadow-blue-500/25 hover:bg-blue-600 transition-colors">Accedi al mio account <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="max-w-6xl mx-auto px-5 py-14">
        <Reveal><h2 className="font-display font-bold text-3xl tracking-tight text-center">Tutto ciò che serve ai tuoi ospiti</h2>
          <p className="text-center text-slate-500 mt-2 max-w-md mx-auto">Ogni informazione importante, bellissima e facile da trovare.</p></Reveal>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 4) * 0.05}>
              <div className="rounded-3xl overflow-hidden border border-slate-100 bg-white shadow-sm h-full">
                <div className="relative h-32">
                  <Img src={f.img} alt={f.title} className="w-full h-full object-cover" />
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-white/90 backdrop-blur rounded-full px-2 py-1 text-[10px] font-bold text-slate-700"><f.icon className="w-3 h-3 text-blue-500" />{f.chip}</span>
                </div>
                <div className="p-3.5"><p className="font-display font-bold text-slate-900">{f.title}</p><p className="text-xs text-slate-500 mt-0.5 leading-snug">{f.desc}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COME FUNZIONA */}
      <HowItWorks />

      <TimeSaved />

      {/* AI CONTENT */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-3 py-1.5"><Wand2 className="w-3.5 h-3.5" /> Contenuti con AI</span>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl tracking-tight">Scrivi meno, ospita meglio</h2>
            <p className="mt-3 text-slate-500">{sb("L'AI trasforma poche informazioni in testi chiari e professionali — messaggi di benvenuto, istruzioni, regole e consigli locali. Tu revisioni e pubblichi.")}</p>
          </Reveal>

          <div className="mt-10 grid lg:grid-cols-2 gap-6 items-stretch">
            <Reveal>
              <div className="rounded-3xl border border-slate-100 bg-white p-6 h-full">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Messaggio di benvenuto</p>
                <div className="mt-3 rounded-2xl bg-slate-50 border border-slate-100 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Prima</p>
                  <p className="text-sm text-slate-500 italic">"Benvenuti nel nostro appartamento…"</p>
                </div>
                <div className="my-3 flex flex-col items-center gap-1">
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-500 rounded-full px-3 py-1.5 shadow-sm shadow-blue-500/25"><Sparkles className="w-3.5 h-3.5" /> Migliora con AI</div>
                  <ChevronDown className="w-4 h-4 text-blue-400" />
                </div>
                <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-blue-500 mb-1">Dopo</p>
                  <p className="text-sm text-slate-700 leading-relaxed">"Benvenuti a Casa Aurora! Abbiamo preparato ogni dettaglio per farvi sentire a casa fin dal primo momento. Qui sotto trovate tutto: Wi-Fi, check-in e i nostri consigli preferiti in zona."</p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="h-full flex flex-col gap-4">
                <div className="rounded-3xl border border-slate-100 bg-white p-6 flex-1">
                  <div className="space-y-5">
                    {[
                      [Wand2, "Genera con AI", "Crea da zero descrizioni, istruzioni e sezioni complete, già in italiano."],
                      [Sparkles, "Migliora i testi", "Rendi ogni messaggio più chiaro, caldo e professionale con un tap."],
                      [Globe, "Traduzioni automatiche", "Offri la guida ai tuoi ospiti anche in inglese, senza sforzo."],
                    ].map(([Icon, t, d]) => (
                      <div key={t} className="flex items-start gap-3.5">
                        <div className="w-11 h-11 rounded-2xl bg-blue-50 text-blue-600 grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
                        <div><p className="font-display font-bold text-base text-slate-900">{t}</p><p className="text-sm text-slate-500 mt-0.5 leading-relaxed">{d}</p></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-3xl border border-slate-100 bg-white p-6">
                  <div className="flex items-center justify-between"><span className="font-semibold text-slate-800 text-sm">La tua guida è completa all'82%</span><span className="text-xs font-semibold text-blue-600">Sa cosa manca</span></div>
                  <div className="mt-2.5 h-2.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: "82%" }} /></div>
                  <div className="mt-4 grid sm:grid-cols-3 gap-2">
                    {[["Wi-Fi", "Aggiungi la password"], ["Parcheggio", "Aggiungi le info"], ["Rifiuti", "Aggiungi le istruzioni"]].map(([a, b]) => (
                      <div key={a} className="rounded-xl bg-slate-50 border border-slate-100 p-3"><span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><span className="w-2 h-2 rounded-full bg-amber-400" />{a}</span><span className="text-xs text-slate-400">{b}</span></div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AI CONCIERGE */}
      <section className="relative py-20">
        <Img src={DEMO.gallery[0]} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-10 items-center">
          <div className="text-white text-center lg:text-left">
            <span className="inline-flex items-center gap-2 text-xs font-semibold bg-white/15 text-white rounded-full px-3 py-1.5"><span className="w-4 h-4 rounded-full bg-white grid place-items-center"><ConciergeIcon className="w-3 h-3" /></span> Concierge AI</span>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl leading-tight">Un Concierge AI sempre disponibile per i tuoi ospiti.</h2>
            <p className="mt-3 text-slate-200 max-w-md mx-auto lg:mx-0">Gli ospiti fanno una domanda, il Concierge AI risponde con le informazioni della tua struttura.</p>
            <Link to="/signup" className="mt-6 inline-flex items-center gap-2 bg-blue-500 text-white font-semibold rounded-full px-6 py-3">Inizia gratis per 7 giorni <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <Reveal className="flex justify-center" delay={0.1}><PhoneChat /></Reveal>
        </div>
      </section>

      <PricingSection billing={billing} setBilling={setBilling} />

      {/* LOCAL GUIDE CAROUSEL */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <Reveal><h2 className="font-display font-bold text-3xl tracking-tight">Scopri i dintorni</h2>
          <p className="text-slate-500 mt-1">I tuoi consigli, in stile travel editoriale.</p></Reveal>
        <div className="mt-6 flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-5 px-5">
          {[[DEMO.restaurant, "Osteria del Centro", "Cucina italiana", "8 min a piedi"], [DEMO.cafe, "Caffè Milano", "Caffetteria", "5 min a piedi"], [DEMO.duomo, "Duomo di Milano", "Da vedere", "12 min"], [DEMO.bar, "Terrazza Aperol", "Aperitivo", "10 min"]].map(([img, n, c, d]) => (
            <div key={n} className="w-64 shrink-0 rounded-3xl overflow-hidden border border-slate-100 bg-white shadow-sm">
              <Img src={img} alt={n} className="w-full h-40 object-cover" />
              <div className="p-4"><span className="text-[11px] font-bold uppercase text-blue-500">{c} · {d}</span><p className="font-serif-display text-xl text-slate-900 leading-tight">{n}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* PRIMA / DOPO */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-5">
          <Reveal><h2 className="font-display font-bold text-3xl tracking-tight text-center">Da informazioni sparse a un'esperienza completa</h2></Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-5 items-center">
            <Reveal>
              <div className="rounded-3xl bg-white border border-slate-100 p-6">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Prima</p>
                <div className="mt-3 flex flex-wrap gap-2">{["WhatsApp", "PDF", "Messaggi", "Note", "Screenshot", "Password Wi-Fi", "Google Maps"].map((x) => <span key={x} className="text-xs bg-slate-100 text-slate-500 rounded-lg px-2.5 py-1.5 line-through decoration-slate-300">{x}</span>)}</div>
                <p className="mt-4 text-sm text-slate-500">Informazioni ovunque, mai aggiornate.</p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="rounded-3xl bg-slate-900 text-white p-6 flex items-center gap-5">
                <div className="scale-[0.6] origin-left -my-10 -mr-16"><PhoneHome mini /></div>
                <div><p className="text-xs font-bold uppercase tracking-wide text-blue-300">Dopo</p><p className="mt-2 font-display font-bold text-xl">Tutto in un unico posto.</p><p className="text-slate-300 text-sm mt-1">Sempre aggiornato.</p></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COMPARAZIONE */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <Reveal><h2 className="font-display font-bold text-3xl tracking-tight text-center">Non è solo una guida digitale</h2></Reveal>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          <Reveal>
            <div className="rounded-3xl border border-slate-100 bg-white p-6 h-full">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 grid place-items-center"><ScrollText className="w-6 h-6 text-slate-400" /></div>
              <h3 className="mt-4 font-display font-bold text-xl text-slate-500">Guida tradizionale</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-500">{["Informazioni statiche", "Poca interazione", "Aggiornamenti manuali"].map((x) => <li key={x} className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300" />{x}</li>)}</ul>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="rounded-3xl border-2 border-blue-500 bg-white p-6 h-full shadow-lg shadow-blue-500/10">
              <div className="w-12 h-12 rounded-2xl bg-blue-500 text-white grid place-items-center"><Sparkles className="w-6 h-6" /></div>
              <h3 className="mt-4 font-display font-bold text-xl">Guida intelligente</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">{["Contenuti dinamici", "AI & QR code", "Consigli locali", "Sempre aggiornata"].map((x) => <li key={x} className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{x}</li>)}</ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PENSATA PER */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal><h2 className="font-display font-bold text-2xl tracking-tight text-center">Pensata per</h2></Reveal>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[["Host", DEMO.cover, HomeIcon], ["Property Manager", DEMO.gallery[7], Building2], ["B&B", DEMO.gallery[1], Star], ["Case vacanza", DEMO.gallery[5], DoorOpen], ["Boutique Hotel", DEMO.gallery[6], Star]].map(([t, img, I]) => (
              <Reveal key={t}><div className="rounded-2xl overflow-hidden border border-slate-100 bg-white"><Img src={img} alt={t} className="w-full h-24 object-cover" /><div className="p-3 flex items-center gap-2"><I className="w-4 h-4 text-blue-500" /><span className="text-sm font-semibold text-slate-700">{t}</span></div></div></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <Reveal><h2 className="font-display font-bold text-3xl tracking-tight text-center">Guarda cosa puoi creare</h2></Reveal>
        <div className="mt-10 flex gap-6 overflow-x-auto hide-scrollbar pb-4 justify-start lg:justify-center -mx-5 px-5">
          <Reveal className="shrink-0"><div className="text-center"><PhoneHome /><p className="mt-3 text-sm font-semibold text-slate-600">Home guida</p></div></Reveal>
          <Reveal className="shrink-0" delay={0.06}><div className="text-center"><PhoneDiscover /><p className="mt-3 text-sm font-semibold text-slate-600">Scopri i dintorni</p></div></Reveal>
          <Reveal className="shrink-0" delay={0.12}><div className="text-center"><PhoneChat /><p className="mt-3 text-sm font-semibold text-slate-600">Concierge AI</p></div></Reveal>
        </div>
      </section>

      <PricingCompact billing={billing} setBilling={setBilling} />

      <FaqSection limit={6} showAllLink />

      {/* FINAL CTA */}
      <section className="max-w-6xl mx-auto px-5 pb-20">
        <div className="rounded-3xl bg-slate-900 text-white p-10 sm:p-14 text-center grain-bg">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight leading-tight">La tua struttura ha già tutto ciò che serve.<br />Ora rendilo semplice da trovare.</h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">Crea la tua Guest Guide, aggiungi il Concierge AI e offri ai tuoi ospiti tutte le informazioni di cui hanno bisogno, direttamente dal loro smartphone.</p>
          <Link to="/signup?plan=trial" data-testid="footer-cta" className="mt-7 inline-flex items-center gap-2 bg-blue-500 text-white font-semibold rounded-full px-7 py-3.5 shadow-lg shadow-blue-500/25">Inizia gratis per 7 giorni <ArrowRight className="w-4 h-4" /></Link>
          <p className="mt-3 text-sm text-slate-300">Crea la tua prima guida in pochi minuti.</p>
          <p className="mt-1 text-sm text-slate-400">{sb("Nessuna app da scaricare. Un link o un QR code, ed è tutto a portata di mano.")}</p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

/* ---------- Phone mockups ---------- */
function Frame({ children }) {
  return <div className="w-[240px] h-[500px] rounded-[38px] bg-slate-900 p-2.5 shadow-2xl shadow-slate-400/40 shrink-0"><div className="w-full h-full rounded-[30px] overflow-hidden bg-[#FDFBF7] hide-scrollbar overflow-y-auto">{children}</div></div>;
}

function renderScene(i) {
  return [<SceneCreate />, <ScenePersonalize />, <ScenePublish />, <SceneShare />][i];
}

function HowItWorks() {
  const [active, setActive] = useState(0);
  const go = (d) => setActive((a) => Math.min(STEPS.length - 1, Math.max(0, a + d)));
  const step = STEPS[active];
  return (
    <section id="come-funziona" className="bg-white py-16 overflow-hidden">
      <div className="max-w-4xl mx-auto px-5">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Come funziona</p>
          <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl tracking-tight">Dalla tua struttura alla guida dei tuoi ospiti</h2>
        </Reveal>

        <div className="mt-9 flex items-center justify-center max-w-md mx-auto" role="tablist" aria-label="Passaggi">
          {STEPS.map((s, i) => (
            <div key={s.n} className={`flex items-center ${i < STEPS.length - 1 ? "flex-1" : ""}`}>
              <button role="tab" aria-selected={i === active} onClick={() => setActive(i)} data-testid={`hiw-step-${i + 1}`} aria-label={`Passo ${s.n} ${s.t}`}
                className={`w-10 h-10 rounded-full grid place-items-center text-sm font-bold transition-all shrink-0 ${i === active ? "bg-blue-600 text-white scale-105 shadow-lg shadow-blue-500/30" : i < active ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-400"}`}>{s.n}</button>
              {i < STEPS.length - 1 && <div className="flex-1 h-1 mx-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-blue-500 transition-all duration-500" style={{ width: i < active ? "100%" : "0%" }} /></div>}
            </div>
          ))}
        </div>

        <div className="mt-8 text-center min-h-[86px]">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
              <h3 className="font-display font-bold text-2xl text-slate-900" data-testid="hiw-title">{step.t}</h3>
              <p className="mt-2 text-slate-500 max-w-md mx-auto">{sb(step.d)}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-4 relative">
          <AnimatePresence mode="wait">
            <motion.div key={active} drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.15}
              onDragEnd={(e, info) => { if (info.offset.x < -60) go(1); else if (info.offset.x > 60) go(-1); }}
              initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.32 }}
              className="cursor-grab active:cursor-grabbing" data-testid="hiw-scene">
              {renderScene(active)}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-6 flex items-center justify-center gap-6">
          <button onClick={() => go(-1)} disabled={active === 0} data-testid="hiw-prev" aria-label="Precedente" className="w-11 h-11 rounded-full border border-slate-200 grid place-items-center text-slate-600 disabled:opacity-30 hover:border-blue-300 hover:text-blue-600 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
          <span className="text-sm font-semibold text-slate-400 tabular-nums">{active + 1} / {STEPS.length}</span>
          <button onClick={() => go(1)} disabled={active === STEPS.length - 1} data-testid="hiw-next" aria-label="Successivo" className="w-11 h-11 rounded-full border border-slate-200 grid place-items-center text-slate-600 disabled:opacity-30 hover:border-blue-300 hover:text-blue-600 transition-colors"><ChevronRight className="w-5 h-5" /></button>
        </div>

        <div className="mt-10 text-center">
          <Link to="/signup?plan=trial" className="inline-flex items-center gap-2 bg-blue-500 text-white font-semibold rounded-full px-7 py-3.5 shadow-lg shadow-blue-500/25 hover:bg-blue-600 transition-colors">Crea la tua guida <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
}

function SceneCreate() {
  return (
    <div className="rounded-[2rem] bg-gradient-to-b from-blue-50 to-white border border-slate-100 px-6 py-8 flex justify-center relative overflow-hidden">
      <div className="w-[240px] rounded-[34px] bg-slate-900 p-2.5 shadow-2xl shadow-slate-400/40 z-10">
        <div className="rounded-[26px] overflow-hidden bg-[#FDFBF7] p-4">
          <p className="font-serif-display text-lg text-[#1F2A44]">Come vuoi iniziare?</p>
          <p className="text-[10px] text-[#7A736A] mt-0.5">Scegli come creare la guida</p>
          <div className="mt-3 space-y-2.5">
            <div className="rounded-2xl border-2 border-blue-500 bg-blue-50/70 p-3">
              <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-blue-600 text-white grid place-items-center"><Sparkles className="w-3.5 h-3.5" /></span><span className="text-xs font-bold text-[#1F2A44]">Crea con l'AI</span><span className="ml-auto text-[8px] font-bold bg-blue-600 text-white px-1.5 py-0.5 rounded-full">Consigliato</span></div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-3 flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-slate-900 text-white grid place-items-center"><Link2 className="w-3.5 h-3.5" /></span><span className="text-xs font-semibold text-[#1F2A44]">Importa da annuncio</span></div>
            <div className="rounded-2xl border border-slate-200 bg-white p-3 flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 grid place-items-center"><PencilRuler className="w-3.5 h-3.5" /></span><span className="text-xs font-semibold text-[#1F2A44]">Crea manualmente</span></div>
          </div>
        </div>
      </div>
      <img src={STEP4_PHOTO} alt="" loading="lazy" className="hidden sm:block absolute bottom-6 right-7 w-40 h-28 object-cover rounded-2xl shadow-xl border-4 border-white rotate-2" />
    </div>
  );
}

function ScenePersonalize() {
  const chips = [["Check-in", DoorOpen, "top-6 left-4"], ["Wi-Fi", Wifi, "top-24 right-5"], ["Regole", ScrollText, "top-1/2 left-3"], ["Servizi", Star, "bottom-28 right-5"], ["Foto", LayoutGrid, "bottom-12 left-7"], ["AI", Sparkles, "top-12 right-8"]];
  return (
    <div className="rounded-[2rem] bg-blue-50/60 border border-slate-100 px-6 py-8 flex justify-center relative overflow-hidden min-h-[440px]">
      {chips.map(([l, I, pos]) => (
        <div key={l} className={`hidden sm:flex absolute ${pos} items-center gap-1.5 bg-white rounded-xl shadow-lg border border-slate-100 px-2.5 py-1.5`}><I className="w-3.5 h-3.5 text-blue-500" /><span className="text-[11px] font-semibold text-slate-700">{l}</span></div>
      ))}
      <div className="w-[240px] rounded-[34px] bg-slate-900 p-2.5 shadow-2xl shadow-slate-400/40 relative z-10">
        <div className="rounded-[26px] overflow-hidden bg-[#FDFBF7] p-4">
          <p className="font-serif-display text-base text-[#1F2A44]">Editor guida</p>
          <div className="mt-3 space-y-2">
            {[["Check-in", DoorOpen], ["Wi-Fi", Wifi], ["La casa", HomeIcon], ["Regole", ScrollText], ["Servizi", Star]].map(([l, I]) => (
              <div key={l} className="flex items-center gap-2.5 rounded-xl bg-white border border-[#F0EDE6] p-2.5"><div className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: "#2E7CB822", color: "#2E7CB8" }}><I className="w-3.5 h-3.5" /></div><span className="text-[11px] font-semibold text-[#1F2A44]">{l}</span><span className="ml-auto text-[9px] font-semibold text-blue-500">Modifica</span></div>
            ))}
            <div className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-blue-200 text-blue-500 py-2 text-[11px] font-semibold"><Sparkles className="w-3 h-3" /> Genera con AI</div>
          </div>
        </div>
      </div>
      <div className="sm:hidden absolute bottom-4 left-0 right-0 flex flex-wrap justify-center gap-1.5 px-4">
        {chips.slice(0, 5).map(([l, I]) => <span key={l} className="inline-flex items-center gap-1 bg-white rounded-full shadow border border-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-600"><I className="w-3 h-3 text-blue-500" />{l}</span>)}
      </div>
    </div>
  );
}

function ScenePublish() {
  return (
    <div className="rounded-[2rem] bg-gradient-to-b from-white to-blue-50 border border-slate-100 px-6 py-8 flex justify-center relative overflow-hidden">
      <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow"><CheckCircle2 className="w-3.5 h-3.5" /> Pubblicata</span>
        <span className="inline-flex items-center gap-1 bg-white text-slate-700 text-[11px] font-bold px-2.5 py-1 rounded-full shadow border border-slate-100"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Online</span>
      </div>
      <div className="mt-6"><PhoneHome /></div>
    </div>
  );
}

function SceneShare() {
  const chips = [["QR Code", QrCode], ["Copia link", Copy], ["Condividi", Share2]];
  return (
    <div className="rounded-[2rem] bg-slate-50 border border-slate-100 p-4 sm:p-6 overflow-hidden">
      <div className="grid sm:grid-cols-2 gap-4 items-center">
        <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
          <img src={STEP4_PHOTO} alt="Struttura luminosa" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute bottom-3 left-3 bg-white rounded-2xl shadow-xl p-2.5 flex items-center gap-2">
            <div className="w-14 h-14 rounded-lg grid place-items-center border border-slate-100"><QrCode className="w-11 h-11 text-slate-900" /></div>
            <div><p className="text-[11px] font-bold text-slate-800 leading-tight">Casa Aurora</p><p className="text-[9px] text-slate-400">Inquadra per aprire</p></div>
          </div>
        </div>
        <div>
          <div className="flex gap-2 flex-wrap">
            {chips.map(([l, I]) => <span key={l} className="inline-flex items-center gap-1.5 bg-white rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700"><I className="w-3.5 h-3.5 text-blue-500" /> {l}</span>)}
          </div>
          <div className="mt-4 rounded-2xl bg-white border border-slate-100 shadow-sm p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500 grid place-items-center text-white"><MessageCircle className="w-5 h-5" /></div>
            <div><p className="text-xs font-semibold text-slate-800">Guida inviata all'ospite</p><p className="text-[11px] text-slate-400">Aperta dal telefono, senza scaricare app</p></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhoneHome() {
  const cards = [["Check-in", DoorOpen], ["Wi-Fi", Wifi], ["Guida della casa", HomeIcon], ["Parcheggio", Car], ["Scopri i dintorni", UtensilsCrossed]];
  const quick = [["Wi-Fi", Wifi], ["Check-in", DoorOpen], ["Indicazioni", MapPin], ["Host", MessageCircle]];
  return (
    <Frame>
      <div className="relative h-36"><Img src={DEMO.cover} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0" style={{ background: "linear-gradient(to top,rgba(15,23,42,.75),transparent)" }} /><div className="absolute bottom-2.5 left-3 text-white"><p className="font-script text-2xl leading-none">Benvenuti a</p><p className="font-serif-display text-base leading-tight">Casa Aurora</p><p className="text-[9px] opacity-90 flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />Milano</p></div></div>
      <div className="px-3 pt-3 grid grid-cols-4 gap-1.5">{quick.map(([l, I]) => <div key={l} className="text-center"><div className="w-9 h-9 rounded-xl grid place-items-center mx-auto" style={{ background: "#2E7CB822", color: "#2E7CB8" }}><I className="w-4 h-4" /></div><span className="block text-[8px] font-semibold mt-1 text-[#1F2A44]">{l}</span></div>)}</div>
      <p className="px-3 pt-4 font-serif-display text-base text-[#1F2A44]">Il tuo soggiorno</p>
      <div className="px-3 pt-1.5 pb-4 space-y-2">{cards.map(([l, I]) => <div key={l} className="flex items-center gap-2.5 rounded-2xl bg-white border border-[#F0EDE6] p-2.5"><div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "#2E7CB822", color: "#2E7CB8" }}><I className="w-4 h-4" /></div><span className="text-xs font-semibold text-[#1F2A44]">{l}</span></div>)}</div>
    </Frame>
  );
}

function PhoneDiscover() {
  const items = [[DEMO.restaurant, "Osteria del Centro", "Cucina italiana · 8 min"], [DEMO.cafe, "Caffè Milano", "Caffetteria · 5 min"], [DEMO.duomo, "Duomo di Milano", "Da vedere · 12 min"]];
  return (
    <Frame>
      <div className="p-3"><p className="font-serif-display text-xl text-[#1F2A44]">Scopri i dintorni</p><p className="text-[10px] text-[#7A736A]">I nostri posti preferiti</p>
        <div className="mt-3 space-y-3">{items.map(([img, n, d]) => <div key={n} className="rounded-2xl bg-white border border-[#F0EDE6] overflow-hidden"><Img src={img} alt="" className="w-full h-24 object-cover" /><div className="p-2.5"><span className="text-[9px] font-bold uppercase text-[#2E7CB8]">{d}</span><p className="font-serif-display text-sm text-[#1F2A44] leading-tight">{n}</p></div></div>)}</div>
      </div>
    </Frame>
  );
}

function PhoneChat() {
  const msgs = [["u", "Qual è la password Wi-Fi?"], ["a", "La rete è CasaAurora_Guest. Ecco la password 👇"], ["copy", "Copia password"], ["u", "Dove possiamo cenare?"], ["a", "L'host consiglia l'Osteria del Centro, a circa 8 minuti a piedi."], ["map", "Apri sulla mappa"]];
  return (
    <Frame>
      <div className="bg-slate-900 text-white px-3 py-3 flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-white grid place-items-center"><ConciergeIcon className="w-4 h-4" /></div><div><p className="text-xs font-semibold">Concierge AI</p><p className="text-[9px] text-emerald-300">● online</p></div></div>
      <div className="p-3 space-y-2">{msgs.map(([who, txt], i) => {
        if (who === "copy") return <button key={i} className="ml-auto flex items-center gap-1.5 rounded-full bg-[#2E7CB8] text-white text-[10px] font-semibold px-3 py-1.5"><Copy className="w-3 h-3" />{txt}</button>;
        if (who === "map") return <button key={i} className="flex items-center gap-1.5 rounded-full bg-white border border-[#2E7CB8] text-[#2E7CB8] text-[10px] font-semibold px-3 py-1.5"><MapPin className="w-3 h-3" />{txt}</button>;
        return <div key={i} className={`max-w-[80%] rounded-2xl px-3 py-2 text-[11px] ${who === "u" ? "ml-auto bg-[#2E7CB8] text-white rounded-br-sm" : "bg-white border border-[#F0EDE6] text-[#1F2A44] rounded-bl-sm"}`}>{txt}</div>;
      })}</div>
    </Frame>
  );
}
